import type {
  CampaignEnrollment,
  CampaignEnrollmentState,
  ClaimCampaign,
  MatchCheckDefinition,
} from '~/services/LuxLoyalty';

export type EnrollmentChipColor = 'info' | 'success' | 'default' | 'error' | 'warning';

interface EnrollmentStatusDisplay {
  label: string;
  color: EnrollmentChipColor;
}

/** How each enrolment state is shown. EXPIRED = no longer checked by the cron. */
export const ENROLLMENT_STATE_DISPLAY: Record<CampaignEnrollmentState, EnrollmentStatusDisplay> = {
  ACTIVE: { label: 'Enrolled', color: 'info' },
  FULFILLED: { label: 'Completed', color: 'success' },
  EXPIRED: { label: 'Expired', color: 'default' },
  CANCELLED: { label: 'Removed', color: 'error' },
};

const ON_TRACK_DISPLAY: EnrollmentStatusDisplay = { label: 'On track', color: 'warning' };

/**
 * Quick-fill checks for a Status Challenge: booked a qualifying stay, and travelled. The query returns
 * booked_nights / completed_nights for every member with a booking in their window.
 */
export const ON_TRACK_CHECKS: Array<MatchCheckDefinition> = [
  { key: 'booked', eligibilityTemplate: '<%= booked_nights >= 5 %>' },
  { key: 'travelled', eligibilityTemplate: '<%= completed_nights >= 5 %>' },
];

/**
 * Adds the on-track checks to the admin's checks without losing any: rows the admin filled in stay as they
 * are, a check with the same key isn't added twice, and empty placeholder rows are dropped.
 */
export function addOnTrackChecks(existing: Array<MatchCheckDefinition>): Array<MatchCheckDefinition> {
  const filled = existing.filter((check) => check.key.trim() || check.eligibilityTemplate.trim());
  const keys = new Set(filled.map((check) => check.key.trim()));
  return [...filled, ...ON_TRACK_CHECKS.filter((check) => !keys.has(check.key)).map((check) => ({ ...check }))];
}

type ProgressEnrollment = Pick<CampaignEnrollment, 'state' | 'checks'>;

/** Only Status Challenges (ADMIN_ONLY) record per-check progress for each member. */
export function tracksCheckProgress(campaign: Pick<ClaimCampaign, 'validatorType'>): boolean {
  return campaign.validatorType === 'ADMIN_ONLY';
}

/** "travelled" → "Travelled", "min_nights" → "Min nights" */
export function humaniseCheckKey(key: string): string {
  const words = key.replace(/[_-]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
}

/**
 * Status chip for an enrolment. On a Status Challenge, an ACTIVE member who passes some checks but not
 * all (e.g. booked, not yet travelled) is "On track". Checks are independent, so this doesn't depend on
 * which ones pass. Passing all of them while still ACTIVE means not rewarded yet (e.g. no Société
 * account), so that stays "Enrolled".
 */
export function getEnrollmentStatusDisplay(
  enrollment: ProgressEnrollment,
  campaign: Pick<ClaimCampaign, 'validatorType'>,
): EnrollmentStatusDisplay {
  if (enrollment.state === 'ACTIVE' && tracksCheckProgress(campaign)) {
    const checks = enrollment.checks ?? [];
    const passed = checks.filter((check) => check.eligible).length;
    if (passed > 0 && passed < checks.length) {
      return ON_TRACK_DISPLAY;
    }
  }
  return ENROLLMENT_STATE_DISPLAY[enrollment.state];
}
